import axios from 'axios';

import {
  parseAddAssetProcessingActivityForBE,
  parseAssetProcessingActivityDetailForFE,
  parseAssetProcessingActivitySubmitForBE,
  parseAssetProcessingActivityUpdateForBE,
  parseOwnerAssetProcessingPurposeForFE,
  parseOwnerAssetProcessingPurposeUpdateForBE,
  parseOwnerCountryHandlingCreateForBE,
  parseOwnerCountryHandlingForFE,
  parseSoftDeleteAssetProcessingActivityForBE,
} from './assetProcessingActivities.parsers';
import type {
  AddAssetProcessingActivityInput,
  AssetProcessingActivitiesApi,
  AssetProcessingActivityDetailDTO,
  AssetProcessingActivitySubmitInput,
  AssetProcessingActivityUpdateInput,
  OwnerAssetProcessingPurposeDTO,
  OwnerAssetProcessingPurposeUpdateInput,
  OwnerCountryHandlingCreateInput,
  OwnerCountryHandlingDTO,
  SoftDeleteAssetProcessingActivityInput,
} from './assetProcessingActivities.types';

const buildApaEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `api/v1/organizations/${organizationId}/asset-processing-activities/${assetProcessingActivityId}/`;

const buildCountriesEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `${buildApaEndpoint(organizationId, assetProcessingActivityId)}countries/`;

const buildCountryEndpoint = (organizationId: number, assetProcessingActivityId: number, countryId: number) =>
  `${buildCountriesEndpoint(organizationId, assetProcessingActivityId)}${countryId}/`;

const buildPurposeEndpoint = (organizationId: number, assetProcessingActivityId: number, purposeId: number) =>
  `${buildApaEndpoint(organizationId, assetProcessingActivityId)}purposes/${purposeId}/`;

const buildSubmitEndpoint = (organizationId: number, assetProcessingActivityId: number) =>
  `${buildApaEndpoint(organizationId, assetProcessingActivityId)}submit/`;

const buildCreateForAssetRopaEndpoint = (organizationId: number, assetRopaId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/${assetRopaId}/processing-activities/`;

const buildSoftDeleteEndpoint = (organizationId: number, assetRopaId: number, assetProcessingActivityId: number) =>
  `api/v1/organizations/${organizationId}/asset-ropa/${assetRopaId}/processing-activities/${assetProcessingActivityId}/soft-delete/`;

export const assetProcessingActivitiesApi: AssetProcessingActivitiesApi = {
  getDetail: async (organizationId, assetProcessingActivityId) => {
    const { data } = await axios.get(buildApaEndpoint(organizationId, assetProcessingActivityId));
    return { data: parseAssetProcessingActivityDetailForFE(data as AssetProcessingActivityDetailDTO) };
  },
  update: async (organizationId, assetProcessingActivityId, payload: AssetProcessingActivityUpdateInput) => {
    const { data } = await axios.patch(
      buildApaEndpoint(organizationId, assetProcessingActivityId),
      parseAssetProcessingActivityUpdateForBE(payload),
    );
    return { data: parseAssetProcessingActivityDetailForFE(data as AssetProcessingActivityDetailDTO) };
  },
  submit: async (organizationId, assetProcessingActivityId, payload: AssetProcessingActivitySubmitInput = {}) => {
    const { data } = await axios.post(
      buildSubmitEndpoint(organizationId, assetProcessingActivityId),
      parseAssetProcessingActivitySubmitForBE(payload),
    );
    return { data: parseAssetProcessingActivityDetailForFE(data as AssetProcessingActivityDetailDTO) };
  },
  createCountry: async (organizationId, assetProcessingActivityId, payload: OwnerCountryHandlingCreateInput) => {
    const { data } = await axios.post(
      buildCountriesEndpoint(organizationId, assetProcessingActivityId),
      parseOwnerCountryHandlingCreateForBE(payload),
    );
    return { data: parseOwnerCountryHandlingForFE(data as OwnerCountryHandlingDTO) };
  },
  deleteCountry: async (organizationId, assetProcessingActivityId, countryId) => {
    await axios.delete(buildCountryEndpoint(organizationId, assetProcessingActivityId, countryId));
  },
  updatePurpose: async (organizationId, assetProcessingActivityId, purposeId, payload: OwnerAssetProcessingPurposeUpdateInput) => {
    const { data } = await axios.patch(
      buildPurposeEndpoint(organizationId, assetProcessingActivityId, purposeId),
      parseOwnerAssetProcessingPurposeUpdateForBE(payload),
    );
    return { data: parseOwnerAssetProcessingPurposeForFE(data as OwnerAssetProcessingPurposeDTO) };
  },
  createForAssetRopa: async (organizationId, assetRopaId, payload: AddAssetProcessingActivityInput) => {
    const { data } = await axios.post(
      buildCreateForAssetRopaEndpoint(organizationId, assetRopaId),
      parseAddAssetProcessingActivityForBE(payload),
    );
    return { data };
  },
  softDelete: async (organizationId, assetRopaId, assetProcessingActivityId, payload: SoftDeleteAssetProcessingActivityInput) => {
    const { data } = await axios.post(
      buildSoftDeleteEndpoint(organizationId, assetRopaId, assetProcessingActivityId),
      parseSoftDeleteAssetProcessingActivityForBE(payload),
    );
    return { data };
  },
};

export default assetProcessingActivitiesApi;
